import { useEffect, useMemo, useState } from "react"
import { useForm } from "react-hook-form"

import { exists } from "shared/lib/exists"
import { useAssignRef } from "shared/lib/useAssignRef"
import { useControlledState } from "shared/lib/useControlledState"
import { useEvent } from "shared/lib/useEvent"
import { ProductPositionInstallationReference } from "shared/types/__generated__/graphql"
import { ProductPositionInstallationReferenceSchema } from "shared/types/__generated__/validation"

import { zodResolver } from "@hookform/resolvers/zod"
import {
  ProductPositionFormData,
  ProductPositionFormSchema,
  ProductPositionFormState
} from "entities/product-positions/types/ProductPositionForm"
import { NO_PRICE_GROUP_NAME } from "entities/projects/lib/formatPriceGroupName"
import { ProjectPermissionType } from "entities/projects/types/ProjectPermissions"
import { ProductPositionFormModel } from "features/product-position-management/types/ProductPositionFormModel"
import { ProductConfiguratorResult } from "widgets/product-configurator/model/useProductConfiguratorModel"
import z from "zod"

import { ProductPositionFormAttachmentsContextType } from "../context/ProductPositionFormAttachmentsContext"
import { ProductPositionFormCoreContextValue } from "../contexts/ProductPositionFormCoreContext"
import { ProductPositionFormFeaturesContextValue } from "../contexts/ProductPositionFormFeaturesContext"
import { ProductPositionFormLoadingContextValue } from "../contexts/ProductPositionFormLoadingContext"
import { ProductPositionFormPriceContextValue } from "../contexts/ProductPositionFormPriceContext"
import { ProductPositionFormProductInformationContextValue } from "../contexts/ProductPositionFormProductInformationContext"
import { ProductPositionFormSelectionContextValue } from "../contexts/ProductPositionFormSelectionContext"

export interface ProductPositionFormViewModel {
  form: ReturnType<
    typeof useForm<ProductPositionFormState, unknown, ProductPositionFormData>
  >
  coreContext: ProductPositionFormCoreContextValue
  loadingContext: ProductPositionFormLoadingContextValue
  priceContext: ProductPositionFormPriceContextValue
  selectionContext: ProductPositionFormSelectionContextValue
  featuresContext: ProductPositionFormFeaturesContextValue
  productInformationContext: ProductPositionFormProductInformationContextValue
  attachmentsContext: ProductPositionFormAttachmentsContextType
}

export function useProductPositionFormViewModel({
  id,
  productPosition,
  defaultProjectID,
  defaultParentID,
  displayPosition,
  isDefaultEditing,
  isEditing,
  onEditingChange,
  price,
  formOptions,
  isSpecialPriceGroup,
  hasProductConfigurationModel,
  isLoadingProductConfigurationModels,
  clarificationRequest,

  permissions,
  isLoadingPermissions,

  formRef,

  productInformation,
  isLoadingProductInformation,
  getFilesCallback,

  attachments,
  attachmentCategories,
  linkedAttachments,
  onUpload,

  onChange,
  onSubmit,
  onCancel,
  openPortfolio,
  openProductConfiguration,
  openProductDimensioning,
  openInstallationReferenceSelection,
  openInstallationReferenceImage,
  deleteInstallationReference,
  ...rest
}: ProductPositionFormModel): ProductPositionFormViewModel {
  const { positionOptions } = rest
  const crContent =
    clarificationRequest?.data.clarificationRequest?.data?.content ?? null
  const priceGroup =
    price?.groups?.[0]?.name === NO_PRICE_GROUP_NAME
      ? null
      : price?.groups?.[0]?.name

  const defaultAttachmentLinks = useMemo(() => {
    return Object.fromEntries(
      linkedAttachments?.map(attachment => [attachment.id, true]) ?? []
    )
  }, [linkedAttachments])

  const defaultFormData: ProductPositionFormState = useMemo(
    () => ({
      productPosition: productPosition || undefined,
      isSpecialPriceGroup,
      parentID: defaultParentID,
      projectID: defaultProjectID,
      displayPosition: displayPosition ?? productPosition?.position ?? 0,
      clarificationRequest: { content: crContent },
      price: price?.itemPrice,
      priceGroup,
      attachments: defaultAttachmentLinks
    }),
    [
      price,
      priceGroup,
      productPosition,
      isSpecialPriceGroup,
      defaultParentID,
      defaultProjectID,
      displayPosition,
      crContent,
      defaultAttachmentLinks
    ]
  )

  const [effectiveEditing, setEffectiveEditing] = useControlledState<boolean>({
    value: isEditing,
    defaultValue: isDefaultEditing,
    onChange: onEditingChange
  })

  const startEditing = useEvent(() => setEffectiveEditing(true))
  const finishEditing = useEvent(() => setEffectiveEditing(false))

  const form = useForm<
    ProductPositionFormState,
    unknown,
    ProductPositionFormData
  >({
    defaultValues: defaultFormData,
    resolver: zodResolver<
      ProductPositionFormState,
      unknown,
      ProductPositionFormData
    >(ProductPositionFormSchema)
  })

  useEffect(() => {
    form.reset(defaultFormData, { keepDirty: true, keepDirtyValues: true })
  }, [defaultFormData])

  useAssignRef({ ref: formRef, value: form })

  const [formData, setFormData] =
    useState<ProductPositionFormState>(defaultFormData)

  useEffect(() => {
    // subscribe() doesn't emit the current values, and the mount-time reset
    // above fires before this subscription exists.
    onChange?.(form.getValues())
    return form.subscribe({
      callback: ({ values: nextFormData }) => {
        setFormData(nextFormData)
        onChange?.(nextFormData)
      },
      formState: { values: true }
    })
  }, [form.subscribe])

  const defaultPosition = useMemo(() => {
    if (
      displayPosition !== undefined &&
      formData?.parentID === defaultParentID
    ) {
      return displayPosition
    }
    if (formData?.parentID === defaultParentID) {
      return productPosition?.position || 0
    }
    if (formData?.parentID === formData?.projectID) {
      return positionOptions?.length ? positionOptions.length - 1 : 0
    }
    return 0
  }, [
    displayPosition,
    formData?.projectID,
    formData?.parentID,
    defaultParentID,
    productPosition?.position,
    positionOptions
  ])

  useEffect(() => {
    form.setValue("displayPosition", defaultPosition)
  }, [defaultPosition])

  useEffect(() => {
    if (formData?.projectID && formData.projectID !== defaultProjectID) {
      form.setValue("parentID", formData?.projectID)
    }
  }, [formData?.projectID])

  const isPriceOverwritten = exists(productPosition?.price)
  const isPriceGroupOverwritten = exists(productPosition?.priceGroup)

  const handleCancel = useEvent(() => {
    form.reset(defaultFormData)
    finishEditing()
    onCancel?.()
  })

  const handleSubmit = useEvent(
    async (submittedFormData: ProductPositionFormData) => {
      let result
      if (onSubmit) {
        const { dirtyFields } = form.formState
        result = await onSubmit({
          ...submittedFormData,
          isSpecialPriceGroup: dirtyFields.isSpecialPriceGroup
            ? submittedFormData.isSpecialPriceGroup
            : undefined,
          price: dirtyFields.price ? submittedFormData.price : undefined,
          priceGroup: dirtyFields.priceGroup
            ? submittedFormData.priceGroup
            : undefined
        })
      }
      if (result?.errors?.length) return
      finishEditing()
    }
  )

  const handleConfigurationChange = useEvent(
    (configuration: ProductConfiguratorResult) => {
      form.setValue("productPosition.variantKey", configuration.variantKey, {
        shouldDirty: !isReadonly
      })
      form.setValue(
        "productPosition.configurationKey",
        configuration.configurationKey || null,
        {
          shouldDirty: !isReadonly
        }
      )
      form.setValue(
        "productPosition.dimensioning",
        configuration.externalDimensioningResult || null,
        {
          shouldDirty: !isReadonly
        }
      )
    }
  )

  const handleInstallationReferencesChange = useEvent(
    (
      productPositionInstallationReferences:
        | ProductPositionInstallationReference[]
        | null
    ) => {
      form.setValue(
        "productPosition.references",
        productPositionInstallationReferences,
        { shouldDirty: true }
      )
    }
  )

  const handleOpenPortfolio = useEvent(() => {
    openPortfolio?.(handleConfigurationChange)
  })

  const handleOpenProductConfiguration = useEvent(() => {
    openProductConfiguration?.(handleConfigurationChange)
  })

  const handleOpenProductDimensioning = useEvent(() => {
    openProductDimensioning?.(handleConfigurationChange)
  })

  const handleOpenInstallationReferenceSelection = useEvent(() => {
    openInstallationReferenceSelection?.(handleInstallationReferencesChange)
  })

  const submitHandler = useMemo(
    () => form.handleSubmit(handleSubmit),
    [form.handleSubmit, handleSubmit]
  )

  const productPositionInstallationReferences = useMemo(() => {
    return (
      (formData.productPosition?.references
        ?.map(
          reference =>
            z.safeParse(ProductPositionInstallationReferenceSchema(), reference)
              .data
        )
        .filter(Boolean) as ProductPositionInstallationReference[]) ?? []
    )
  }, [formData.productPosition?.references])

  const hideSpecialPriceGroupToggle =
    !hasProductConfigurationModel ||
    !permissions[ProjectPermissionType.CREATE_SPECIAL_PRICE_GROUP]

  // While permissions load they read as readonly, which would flash the form as
  // a read-only view. Disable it instead until the real permissions arrive.
  const isReadonly =
    !effectiveEditing ||
    (!isLoadingPermissions && !permissions[ProjectPermissionType.EDIT])

  const isDisabled = rest.isDisabled || isLoadingPermissions

  const coreContext = useMemo<ProductPositionFormCoreContextValue>(
    () => ({
      id,
      isReadonly,
      isDisabled,
      formOptions,
      permissions,
      onSubmit: submitHandler,
      onCancel: handleCancel,
      onEdit: onSubmit ? startEditing : undefined
    }),
    [
      id,
      isReadonly,
      isDisabled,
      formOptions,
      permissions,
      submitHandler,
      handleCancel,
      onSubmit,
      startEditing
    ]
  )

  const loadingContext = useMemo<ProductPositionFormLoadingContextValue>(
    () => ({
      isLoading: rest.isLoading,
      isLoadingPrice: rest.isLoadingPrice,
      isLoadingSpecialPriceGroups: rest.isLoadingSpecialPriceGroups,
      isLoadingParentOptions: rest.isLoadingParentOptions,
      isLoadingPositionOptions: rest.isLoadingPositionOptions,
      isLoadingProjectOptions: rest.isLoadingProjectOptions,
      isLoadingPriceGroupOptions: rest.isLoadingPriceGroupOptions,
      isLoadingInstallationReferences: rest.isLoadingInstallationReferences
    }),
    [
      rest.isLoading,
      rest.isLoadingPrice,
      rest.isLoadingSpecialPriceGroups,
      rest.isLoadingParentOptions,
      rest.isLoadingPositionOptions,
      rest.isLoadingProjectOptions,
      rest.isLoadingPriceGroupOptions,
      rest.isLoadingInstallationReferences
    ]
  )

  const priceContext = useMemo<ProductPositionFormPriceContextValue>(
    () => ({
      price,
      isPriceOverwritten,
      isPriceGroupOverwritten,
      isSpecialPriceGroup,
      isPriceGroupEditable: permissions[ProjectPermissionType.EDIT_PRICE_GROUP],
      isUnitPriceEditable: permissions[ProjectPermissionType.EDIT_PRICE],
      isPriceServiceAvailable: rest.isPriceServiceAvailable,
      hideSpecialPriceGroupToggle,
      priceGroupOptions: rest.priceGroupOptions
    }),
    [
      price,
      isPriceOverwritten,
      isPriceGroupOverwritten,
      isSpecialPriceGroup,
      permissions,
      rest.isPriceServiceAvailable,
      hideSpecialPriceGroupToggle,
      rest.priceGroupOptions
    ]
  )

  const selectionContext = useMemo<ProductPositionFormSelectionContextValue>(
    () => ({
      projectOptions: rest.projectOptions,
      roomOptions: rest.roomOptions,
      positionOptions: rest.positionOptions
    }),
    [rest.projectOptions, rest.roomOptions, rest.positionOptions]
  )

  const featuresContext = useMemo<ProductPositionFormFeaturesContextValue>(
    () => ({
      formData,
      isConfigurationDisabled: isLoadingProductConfigurationModels,
      openPortfolio: openPortfolio ? handleOpenPortfolio : undefined,
      openProductConfiguration: openProductConfiguration
        ? handleOpenProductConfiguration
        : undefined,
      openProductDimensioning: openProductDimensioning
        ? handleOpenProductDimensioning
        : undefined,
      installationReferences: rest.installationReferences,
      productPositionInstallationReferences,
      openInstallationReferenceSelection:
        handleOpenInstallationReferenceSelection,
      openInstallationReferenceImage,
      deleteInstallationReference: deleteInstallationReference
        ? (refId: string) =>
            deleteInstallationReference(
              refId,
              handleInstallationReferencesChange
            )
        : undefined,
      clarificationRequest,
      hasClarificationRequest: productPosition?.hasClarificationRequest ?? false
    }),
    [
      formData,
      isLoadingProductConfigurationModels,
      openPortfolio,
      handleOpenPortfolio,
      openProductConfiguration,
      handleOpenProductConfiguration,
      openProductDimensioning,
      handleOpenProductDimensioning,
      rest.installationReferences,
      productPositionInstallationReferences,
      handleOpenInstallationReferenceSelection,
      openInstallationReferenceImage,
      deleteInstallationReference,
      handleInstallationReferencesChange,
      clarificationRequest,
      productPosition?.hasClarificationRequest
    ]
  )

  const productInformationContext =
    useMemo<ProductPositionFormProductInformationContextValue>(
      () => ({
        productInformation,
        isLoadingProductInformation,
        getFilesCallback
      }),
      [productInformation, isLoadingProductInformation, getFilesCallback]
    )

  const attachmentsContext = useMemo<ProductPositionFormAttachmentsContextType>(
    () => ({
      attachments,
      attachmentCategories,
      linkedAttachments,
      onUpload
    }),
    [attachments, attachmentCategories, linkedAttachments, onUpload]
  )

  return {
    form,
    coreContext,
    loadingContext,
    priceContext,
    selectionContext,
    featuresContext,
    productInformationContext,
    attachmentsContext
  }
}
